/**
 * Where each workspace's folder belongs in the local folder: under a folder named after the
 * account (accountFolderName), then the same hierarchy as the app's library, Project / Folder… /
 * Workspace, each name cleaned for the disk. A workspace in no project sits right under the
 * account's folder; one shared with the person sits under Shared_with_me (the owner's projects
 * are not theirs to see).
 *
 *   {root}/ai@example.com/Ocean_film/Drafts/Storm_test/Videos/videos_1.mp4
 *
 * The account's folder keeps two accounts that chose the same folder on one computer apart, and
 * says whose files are whose. Folders made before this sat at the top: named after their
 * workspace at first, then under their projects. planFolderMoves works out which folders to move
 * to their place, and refuses whenever the answer is not certain: a name two workspaces share, a
 * folder that is also a project's, a place another workspace already has. What is not moved is
 * still found where it is (localSave's lookups try the old places too).
 *
 * Pure: localSave.ts does the reading and the moving.
 */
import { getFolderPath } from './folderTree';
import type { WorkspaceFolder } from './workspaceFolders';

/** The top folder of workspaces other people share with the person */
export const SHARED_FOLDER = 'Shared_with_me';
/** Top folders the app keeps for itself, which are never a workspace's */
export const RESERVED_TOP_FOLDERS = [SHARED_FOLDER, 'workflows'];

/** A name made safe for a folder on any disk: the app's long-standing cleaning, plus control characters */
export const folderSegment = (name: string): string =>
    name
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
        .replace(/\s+/g, '_')
        .substring(0, 50) || 'Untitled';

/**
 * The folder an account's files go under, inside the local folder: the address the person signs
 * in with, in lower case and cleaned for the disk, so it is the same on every computer and says
 * whose files these are. An account without an address goes under its name, or under its id when
 * the name is the stand-in "User" (two accounts could have that one).
 */
export const accountFolderName = (user: { sub: string; email?: string | null; name?: string | null }): string => {
    const email = (user.email || '').trim().toLowerCase();
    const name = (user.name || '').trim();
    return folderSegment(email || (name && name !== 'User' ? name : user.sub));
};

export interface WorkspacePlace {
    id: string;
    name: string;
    /** The folders from the account's folder down, the workspace's own last */
    dir: string[];
}

export const placeKey = (dir: string[]): string => dir.join('/');

interface PlaceableWorkspace {
    id: string;
    name: string;
    folderId: string | null;
    isShared: boolean;
}

/**
 * Every workspace's place, from the library. A workspace whose project or folder is not in the
 * list (not read yet, or gone) has no place here: nothing is decided for it until it is known.
 */
export const placesFromLibrary = (workspaces: ReadonlyArray<PlaceableWorkspace>, folders: WorkspaceFolder[]): WorkspacePlace[] =>
    workspaces.flatMap(workspace => {
        const own = folderSegment(workspace.name);
        if (workspace.isShared) return [{ id: workspace.id, name: workspace.name, dir: [SHARED_FOLDER, own] }];
        if (!workspace.folderId) return [{ id: workspace.id, name: workspace.name, dir: [own] }];
        const chain = getFolderPath(folders, workspace.folderId);
        if (chain.length === 0 || chain[chain.length - 1].id !== workspace.folderId) return [];
        return [{ id: workspace.id, name: workspace.name, dir: [...chain.map(folder => folderSegment(folder.name)), own] }];
    });

export interface FolderMove {
    id: string;
    from: string[];
    to: string[];
}

/**
 * The folders to move so each workspace's sits at its place. `remembered` is where the app last
 * put each workspace's folder (by id); `exists` tells which of the candidate folders are on disk.
 * A workspace's folder is looked for where it was last put, then at its place at the top (from
 * before accounts had folders of their own), then at the top under its own name (where every
 * folder was before); it is moved only when that is certainly its folder.
 */
export const planFolderMoves = (
    places: ReadonlyArray<WorkspacePlace>,
    remembered: Readonly<Record<string, string>>,
    exists: (dir: string[]) => boolean,
    /** Names of workspaces with no place yet: an old top folder of one of their names is not certainly anyone's */
    unplacedNames: ReadonlyArray<string> = [],
    /** The account's folder, which every place is under (accountFolderName); none before accounts had folders of their own */
    top: ReadonlyArray<string> = [],
): FolderMove[] => {
    const dirOf = (place: WorkspacePlace): string[] => [...top, ...place.dir];
    const targets = new Map<string, number>();
    for (const place of places) targets.set(placeKey(dirOf(place)), (targets.get(placeKey(dirOf(place))) ?? 0) + 1);
    // Every folder that holds some workspace's place, or lies on the way to one (a project's, a
    // folder's), at the top too where the places were before they went under the account's folder
    const used = new Set<string>();
    for (const place of places) {
        const dir = dirOf(place);
        for (let i = 1; i <= dir.length; i++) used.add(placeKey(dir.slice(0, i)));
        if (top.length > 0) for (let i = 1; i < place.dir.length; i++) used.add(placeKey(place.dir.slice(0, i)));
    }
    // How many workspaces would claim each old top folder
    const tops = new Map<string, number>();
    for (const name of [...places.map(place => place.name), ...unplacedNames]) tops.set(folderSegment(name), (tops.get(folderSegment(name)) ?? 0) + 1);

    const moves: FolderMove[] = [];
    const taken = new Set<string>();
    for (const place of places) {
        const to = dirOf(place);
        const target = placeKey(to);
        // Two workspaces with one place (the same name in the same folder): which folder is whose is not certain
        if ((targets.get(target) ?? 0) > 1) continue;
        const candidates: string[][] = [];
        const before = remembered[place.id];
        if (before && before !== target) candidates.push(before.split('/'));
        // Its place at the top, from before accounts had folders of their own (a folder at the very top is judged by its name, below)
        if (top.length > 0 && place.dir.length > 1) candidates.push(place.dir);
        const topName = folderSegment(place.name);
        if (topName !== target
            && (tops.get(topName) ?? 0) === 1
            && !used.has(topName)
            && !RESERVED_TOP_FOLDERS.some(name => name.toLowerCase() === topName.toLowerCase())) {
            candidates.push([topName]);
        }
        const seen = new Set<string>();
        const from = candidates.filter(dir => !seen.has(placeKey(dir)) && !!seen.add(placeKey(dir))).find(dir => {
            const key = placeKey(dir);
            if (taken.has(key) || !exists(dir)) return false;
            // A folder that is now another workspace's place, or on the way to one, is left alone
            if (used.has(key)) return false;
            // Never into itself, nor around itself
            return !target.startsWith(`${key}/`) && !key.startsWith(`${target}/`);
        });
        if (!from) continue;
        taken.add(placeKey(from));
        moves.push({ id: place.id, from, to });
    }
    return moves;
};
